import { useCallback, useEffect, useState } from "react";
import { UnifiedMessage } from "@/hooks/use-messages";
import { useGlobalState } from "@/hooks";
import { CartProduct, Product } from "@/types/product";
import { ProductDetails } from "./ProductDetails";
import { LastUserMessage } from "./LastUserMessage";
import { ProductList } from "./ProductList";
import { getProductVariantData } from "@/utils/get-product-variant-data";
import { VoiceAgentStateCircleContainer } from "./VoiceAgentStateCircleContainer";
import { VoiceAgentStateIndicator } from "./VoiceAgentStateIndicator";
import { AgentState } from "@livekit/components-react";
import { VoiceAgentStateCapsuleContainer } from "./VoiceAgentStateCapsuleContainer";
import { CustomAudioRenderer } from "@/providers/LiveKitProvider";
import { SHOPPING_ASSISTANT_GREETING } from "@/configs/greeting";

const INTRODUCTION_HTML_ASK_MICROPHONE_PERMISSION = "Enable mic to continue";
const INTRODUCTION_HTML = {
  __html:
    "Connecting to your personal assistant… <br/>Please hold on while we prepare everything for you.",
};

type VoiceModeProps = {
  messages: UnifiedMessage[];
  agentState: AgentState;
  productList: Product[];
  selectedProduct: Product;
  onSelectProduct: (product: Product | null) => void;
  cartItems: CartProduct[];
  onAddToCart: (product: Product) => void;
  onCheckout: () => void;
};

export const VoiceMode = ({
  messages,
  agentState,
  productList,
  selectedProduct,
  onSelectProduct,
  cartItems,
  onAddToCart,
  onCheckout,
}: VoiceModeProps) => {
  const { chatMode, micPermission, setMicPermission } = useGlobalState();
  const [lastUserMessage, setLastUserMessage] = useState<string>("");
  const { variantToShow, image, formattedPrice } =
    getProductVariantData(selectedProduct);

  // Set a listener for microphone permission changes
  useEffect(() => {
    let permissionStatus: PermissionStatus;

    // Observe Permissions in non-iOS devices (not supported on iOS)
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: "microphone" as PermissionName })
        .then((status) => {
          permissionStatus = status;
          setMicPermission(status.state);

          status.onchange = () => {
            setMicPermission(status.state);
          };
        });

      return () => {
        if (permissionStatus) {
          permissionStatus.onchange = null;
        }
      };
    }
  }, [setMicPermission]);

  useEffect(() => {
    const userMessages = messages.filter((message) => {
      const isFromUser = message.isUser || message.isSelf;
      const isProductMessage =
        message.message?.startsWith("products_") &&
        message.text?.startsWith("products_");

      return isFromUser && !isProductMessage;
    });

    if (userMessages.length > 0) {
      const userMessageAtLastIndex = userMessages[userMessages.length - 1];
      const messageContent =
        userMessageAtLastIndex.text || userMessageAtLastIndex.message || "";

      setLastUserMessage(messageContent);
    }
  }, [chatMode, messages]);

  const handleYesClick = useCallback(() => {
    onAddToCart(selectedProduct);
  }, [onAddToCart, selectedProduct?.id]);

  return (
    <>
      {(productList?.length > 0 || selectedProduct) && (
        <VoiceAgentStateCapsuleContainer agentState={agentState}>
          <VoiceAgentStateIndicator agentState={agentState} />
        </VoiceAgentStateCapsuleContainer>
      )}
      {lastUserMessage && <LastUserMessage text={lastUserMessage} />}
      <div
        className={`chat-body nut-flex-1 nut-overflow-y-auto nut-flex nut-flex-col nut-items-center ${productList.length > 0 || selectedProduct ? "nut-justify-start" : "nut-justify-center nut-pt-[20px]"}`}
      >
        {productList.length === 0 && !selectedProduct && (
          <p className="text-chat-introduction nut-text-center nut-text-[18px] nut-leading-[150%] nut-tracking-[0%] nut-align-middle nut-px-4 nut-mb-4">
            {SHOPPING_ASSISTANT_GREETING}
          </p>
        )}

        {micPermission !== "granted" && (
          <>
            <p className="text-chat-introduction nut-text-center nut-text-[22px] nut-leading-[150%] nut-tracking-[0%] nut-align-middle">
              {INTRODUCTION_HTML_ASK_MICROPHONE_PERMISSION}
            </p>
            <br />
          </>
        )}

        {agentState === "connecting" && (
          <p
            className="text-chat-introduction nut-text-center nut-text-[18px] nut-leading-[150%] nut-tracking-[0%] nut-align-middle"
            dangerouslySetInnerHTML={INTRODUCTION_HTML}
          ></p>
        )}

        {productList.length === 0 &&
          ["listening", "thinking", "speaking"].includes(agentState) && (
            <VoiceAgentStateCircleContainer agentState={agentState}>
              <VoiceAgentStateIndicator agentState={agentState} />
            </VoiceAgentStateCircleContainer>
          )}

        {/* product */}
        {productList?.length > 0 && !selectedProduct && (
          <>
            <div>
              <ProductList
                onSelectProduct={onSelectProduct}
                productList={productList}
              />
            </div>
          </>
        )}

        {/* show product description */}
        {selectedProduct && (
          <>
            <div className="widget-displaying-results nut-w-[100%]">
              <ProductDetails
                image={image}
                title={selectedProduct.title}
                currentPrice={formattedPrice || "N/A"}
                onYesClick={handleYesClick}
                onNoClick={() => {
                  onSelectProduct(null);
                }}
                onCheckoutClick={onCheckout}
                productUrl={
                  selectedProduct.product_url ||
                  (selectedProduct.store_url && selectedProduct.handle
                    ? `${selectedProduct.store_url.replace(/\/$/, "")}/products/${selectedProduct.handle}`
                    : undefined)
                }
                isAddedToCart={cartItems.some(
                  (item) => item.variant_id === variantToShow?.id,
                )}
              />
            </div>
          </>
        )}
      </div>
      <CustomAudioRenderer />
    </>
  );
};
